import React, { useState, useEffect } from 'react';
import { useJWTAuth } from '../auth/JWTAuthContext';
import { useToast } from '../contexts/ToastContext';

const PLATFORMS = [
  { id: 'pumpfun', name: 'PumpFun', icon: '/assets/platform/platform-pumpfun.png' },
  { id: 'x', name: 'X (Twitter)', icon: '/assets/platform/platform-x.png' },
  { id: 'meta', name: 'Meta', icon: '/assets/platform/platform-meta.png' },
  { id: 'twitch', name: 'Twitch', icon: '/assets/platform/platform-twitch.png' },
  { id: 'tiktok', name: 'TikTok', icon: '/assets/platform/platform-tiktok.png' },
  { id: 'youtube', name: 'YouTube', icon: '/assets/platform/platform-youtube.png' }
];

// Fallback emoji for platforms without icons
const PLATFORM_EMOJIS = {
  pumpfun: '🚀',
  x: '🐦',
  meta: '📘',
  twitch: '🎮',
  tiktok: '🎵',
  youtube: '📺'
};

const AccountLinkingSection = () => {
  const { getAuthHeaders, isAuthenticated } = useJWTAuth();
  const { success: showSuccessToast, error: showErrorToast } = useToast();
  const [linkedPlatforms, setLinkedPlatforms] = useState([]);
  const [availablePlatforms, setAvailablePlatforms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generatedCodes, setGeneratedCodes] = useState({}); // { platform: { code, expiresAt } }
  const [countdowns, setCountdowns] = useState({}); // { platform: secondsRemaining }

  useEffect(() => {
    if (isAuthenticated) {
      loadLinkedPlatforms();
      loadAvailablePlatforms();
    }
  }, [isAuthenticated]);

  // Listen for account claim events to refresh the linked platforms
  useEffect(() => {
    const handleAccountClaimed = (event) => {
      // Refresh linked platforms when an account is claimed
      loadLinkedPlatforms();
      loadAvailablePlatforms();
      // Clear any generated codes for the claimed platform
      if (event.detail?.platform) {
        setGeneratedCodes(prev => {
          const updated = { ...prev };
          delete updated[event.detail.platform];
          return updated;
        });
      }
    };

    window.addEventListener('account-claimed', handleAccountClaimed);
    return () => {
      window.removeEventListener('account-claimed', handleAccountClaimed);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Countdown timer effect
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      const newCountdowns = {};
      
      Object.keys(generatedCodes).forEach(platform => {
        const expiresAt = new Date(generatedCodes[platform].expiresAt);
        const secondsRemaining = Math.max(0, Math.floor((expiresAt - now) / 1000));
        newCountdowns[platform] = secondsRemaining;
        
        if (secondsRemaining === 0) {
          // Code expired, remove it
          setGeneratedCodes(prev => {
            const updated = { ...prev };
            delete updated[platform];
            return updated;
          });
        }
      });
      
      setCountdowns(newCountdowns);
    }, 1000);

    return () => clearInterval(interval);
  }, [generatedCodes]);

  const loadLinkedPlatforms = async () => {
    try {
      const headers = getAuthHeaders ? getAuthHeaders() : {};
      const response = await fetch('/api/account-links/linked-platforms', {
        headers
      });
      const data = await response.json();
      
      if (data.success) {
        setLinkedPlatforms(data.platforms || []);
      }
    } catch (error) {
      console.error('Error loading linked platforms:', error);
    }
  };

  const loadAvailablePlatforms = async () => {
    try {
      const headers = getAuthHeaders ? getAuthHeaders() : {};
      const response = await fetch('/api/account-links/available-platforms', {
        headers
      });
      const data = await response.json();
      
      if (data.success) {
        setAvailablePlatforms(data.platforms || []);
      }
    } catch (error) {
      console.error('Error loading available platforms:', error);
    }
  };

  const generateClaimCode = async (platform) => {
    setLoading(true);
    try {
      const headers = getAuthHeaders ? getAuthHeaders() : {};
      const response = await fetch('/api/account-links/generate-code', {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ platform })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setGeneratedCodes(prev => ({
          ...prev,
          [platform]: {
            code: data.claimCode,
            expiresAt: data.expiresAt
          }
        }));
        // Automatically copy the claim command to clipboard
        const claimCommand = `Claim ${data.claimCode}`;
        await copyToClipboard(claimCommand, false);
        showSuccessToast(`Claim code generated and copied to clipboard!`);
      } else {
        showErrorToast(data.error || 'Failed to generate claim code');
      }
    } catch (error) {
      console.error('Error generating claim code:', error);
      showErrorToast('Failed to generate claim code');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, showToast = true) => {
    try {
      await navigator.clipboard.writeText(text);
      if (showToast) {
        showSuccessToast('Claim code copied to clipboard!');
      }
    } catch (error) {
      console.error('Failed to copy:', error);
      if (showToast) {
        showErrorToast('Failed to copy to clipboard');
      }
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="bg-black bg-opacity-60 backdrop-blur-sm rounded-xl p-8 border border-white border-opacity-20 md:col-span-2">
      <div className="text-center mb-6">
        <div className="text-6xl mb-4">🔗</div>
        <h2 className="text-2xl font-bold text-white mb-2">Account Linking</h2>
        <p className="text-gray-300">
          Link your social media accounts to claim your auto-created heroes
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {PLATFORMS.map(platform => {
          const isLinked = linkedPlatforms.includes(platform.id);
          const hasGeneratedCode = generatedCodes[platform.id];
          const countdown = countdowns[platform.id] || 0;
          const isAvailable = availablePlatforms.includes(platform.id);

          return (
            <div
              key={platform.id}
              className={`bg-gray-800 bg-opacity-50 rounded-lg p-4 border ${
                isLinked
                  ? 'border-green-500'
                  : hasGeneratedCode
                  ? 'border-yellow-500'
                  : 'border-gray-600'
              }`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="relative w-10 h-10 flex items-center justify-center">
                  <img
                    src={platform.icon}
                    alt={platform.name}
                    className="w-10 h-10 rounded"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const fallback = e.target.parentElement.querySelector('.platform-fallback');
                      if (fallback) fallback.style.display = 'flex';
                    }}
                  />
                  <div className="platform-fallback absolute inset-0 items-center justify-center text-2xl" style={{ display: 'none' }}>
                    {PLATFORM_EMOJIS[platform.id] || '🔗'}
                  </div>
                </div>
                <span className="text-white font-semibold">{platform.name}</span>
                {isLinked && (
                  <span className="ml-auto text-green-400 text-sm">✓ Linked</span>
                )}
              </div>

              {!isLinked && hasGeneratedCode ? (
                <div className="space-y-3">
                  <div className="bg-gray-900 rounded p-3 text-center">
                    <div className="text-xs text-gray-400 mb-1">Claim Code</div>
                    <div className="text-2xl font-mono font-bold text-yellow-400 mb-2">
                      {generatedCodes[platform.id].code}
                    </div>
                    <div className="text-xs text-gray-400">
                      Expires in: <span className="text-yellow-400">{formatTime(countdown)}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => copyToClipboard(`Claim ${generatedCodes[platform.id].code}`)}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-3 rounded text-sm transition-colors"
                    >
                      Copy
                    </button>
                    <button
                      onClick={() => generateClaimCode(platform.id)}
                      disabled={loading}
                      className="flex-1 bg-gray-600 hover:bg-gray-700 disabled:opacity-50 text-white font-bold py-2 px-3 rounded text-sm transition-colors"
                    >
                      Regenerate
                    </button>
                  </div>
                  <div className="text-xs text-gray-400 text-center">
                    Enter <span className="font-mono text-yellow-400">Claim {generatedCodes[platform.id].code}</span> in {platform.name} chat
                  </div>
                </div>
              ) : isAvailable ? (
                <button
                  onClick={() => generateClaimCode(platform.id)}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 text-white font-bold py-2 px-3 rounded transition-colors"
                >
                  {loading ? 'Generating...' : 'Generate Claim Code'}
                </button>
              ) : (
                <div className="text-center py-2">
                  
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-6 p-4 bg-gray-800 bg-opacity-50 rounded-lg">
        <div className="text-sm text-gray-300">
          <div className="font-semibold mb-2">How it works:</div>
          <ol className="list-decimal list-inside space-y-1 text-gray-400">
            <li>Click "Generate Claim Code" for the platform you want to link</li>
            <li>Copy the 8-character code</li>
            <li>Go to that platform's chat and type: <span className="font-mono text-yellow-400">Claim YOURCODE</span></li>
            <li>Your auto-created hero will be transferred to your main account</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default AccountLinkingSection;

